import { IconType } from "react-icons";
import {
  FcCommandLine,
  FcElectronics,
  FcTreeStructure,
} from "react-icons/fc/index.js";
import { FiCloud, FiServer, FiShield } from "react-icons/fi/index.js";
import { MdEmail, MdLocationOn, MdPhone } from "react-icons/md/index.js";

export type FCardProps = [
  heading: string,
  description: string,
  icon: undefined | IconType,
  features: string[],
  img: string,
  cta: string
];

export interface ProblemSolution {
  problem: string;
  title: string;
  description: string;
  recommendations: Array<{
    text: string;
    details: string;
    icon: IconType; // from react-icons
    metrics?: {
      value: string;
      label: string;
    };
  }>;
  ctaText: string;
  estimatedTimeFrame: string;
  relevantCaseStudy?: {
    company: string;
    result: string;
    link: string;
  };
  technologies: string[];
}
export interface Partner {
  name: string;
  logo: string;
  category: "Cloud" | "Security" | "Hardware" | "Communications" | "Software";
  solutions: string[];
}
export interface AssessmentType {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  questions: string[];
  estimatedTime: string;
}

export const ASSESSMENT_TYPES: AssessmentType[] = [
  {
    id: "infrastructure",
    title: "Infrastructure Assessment",
    description:
      "Evaluate your current IT infrastructure and identify optimization opportunities",
    icon: FiServer,
    questions: [
      "Current infrastructure challenges",
      "Growth projections",
      "Performance requirements",
      "Budget considerations",
    ],
    estimatedTime: "15 min",
  },
  {
    id: "security",
    title: "Security Assessment",
    description: "Review your security posture and compliance readiness",
    icon: FiShield,
    questions: [
      "Security concerns",
      "Compliance requirements",
      "Current security measures",
      "Risk tolerance",
    ],
    estimatedTime: "20 min",
  },
  {
    id: "cloud",
    title: "Cloud Readiness",
    description: "Assess your organization's cloud migration potential",
    icon: FiCloud,
    questions: [
      "Current cloud usage",
      "Migration goals",
      "Application inventory",
      "Data requirements",
    ],
    estimatedTime: "15 min",
  },
];

export const PARTNER_CATEGORIES = {
  Cloud: "Cloud & Infrastructure",
  Security: "Security & Compliance",
  Hardware: "Enterprise Hardware",
  Communications: "UCaaS & CCaaS",
  Software: "Software Solutions",
};

export const FEATURED_PARTNERS: Partner[] = [
  {
    name: "Lenovo",
    logo: "/img/partners/lenovo.webp",
    category: "Hardware",
    solutions: ["ThinkSystem Servers", "Edge Computing", "Smart Devices"],
  },
  {
    name: "Meraki",
    logo: "/img/partners/meraki.webp",
    category: "Security",
    solutions: ["SD-WAN", "Wireless", "Network Security"],
  },
  // ... other partners
];

export const PROBLEM_SOLUTIONS: ProblemSolution[] = [
  {
    problem: "Need to scale IT infrastructure",
    title: "Enterprise IT Infrastructure",
    description:
      "Scale your business with confidence using our enterprise-grade IT solutions designed for growing organizations.",
    recommendations: [
      {
        text: "Cloud Migration Services",
        details:
          "Seamless transition to cloud infrastructure with zero downtime",
        icon: FiCloud,
        metrics: {
          value: "99.99%",
          label: "Uptime guarantee",
        },
      },
      {
        text: "Hybrid Infrastructure Design",
        details:
          "Custom-designed infrastructure combining on-premise and cloud solutions",
        icon: FiServer,
        metrics: {
          value: "40%",
          label: "Average cost reduction",
        },
      },
      // ... more recommendations
    ],
    ctaText: "Schedule Infrastructure Assessment",
    estimatedTimeFrame: "2-4 weeks",
    relevantCaseStudy: {
      company: "Regional Healthcare Provider",
      result: "Scaled from 3 to 15 locations in 6 months",
      link: "/case-studies/healthcare-scaling",
    },
    technologies: ["AWS", "Azure", "VMware", "Cisco Meraki"],
  },
  // {
  //   problem: "Managing multiple locations",
  //   title: "Multi-Location Network Management",
  //   description: "Unified network solutions that keep your locations connected, secure, and efficiently managed.",
  //   recommendations: [
  //     "SD-WAN Implementation",
  //     "UCaaS Solutions",
  //     "Centralized Management",
  //     "Remote Site Support"
  //   ],
  //   ctaText: "Get Network Analysis"
  // },
  // {
  //   problem: "Cybersecurity concerns",
  //   title: "Enterprise Security Solutions",
  //   description: "Comprehensive security measures to protect your business across all locations and endpoints.",
  //   recommendations: [
  //     "Penetration Testing",
  //     "Zero Trust Architecture",
  //     "Compliance Management",
  //     "Security Training"
  //   ],
  //   ctaText: "Request Security Audit"
  // }
];

export const CASE_STUDIES = [
  {
    slug: "determined-by-design",
    title: "In-House Drafting Transformation",
    imgSrc: "/img/clients/dbd.webp",
    uri: "https://determinedbydesign.com/",
    companyName: "Determined by Design",
    companyDesc:
      "Interior designers creating inclusive spaces, especially for Black and Brown communities.",
    summary: `To streamline projects, Determined By Design shifted to in-house drafting and rendering using Autodesk Revit. TenK Solutions provided hardware setup and network support to empower growth and ensure seamless operation. While the solution achieved its technical objectives, post-implementation feedback highlighted opportunities for future cloud-based alternatives. The implementation delivered:

- Successfully deployed high-performance server and workstations for Revit/3D rendering
- Cut outsourcing costs by approximately $45,000 annually
- Implemented automated daily backups of project files
- Enabled concurrent work on up to 3 major projects
- Supported files up to 2GB with no performance degradation
- Enhanced quality control through in-house revision capability

Lessons Learned & Future Recommendations:
- On-premise server noise impact on office environment exceeded expectations
- Cloud-based solutions could offer similar performance with reduced environmental impact

These improvements significantly enhanced the firm's design capabilities while highlighting valuable insights for future technology planning.`,
  },
  {
    slug: "why-architecture",
    title: "Seamless Remote Operations for Architecture",
    imgSrc: "/img/clients/why.webp",
    uri: "https://why-site.com/",
    companyName: "wHY Architecture",
    companyDesc:
      "Architecture studio connecting people, culture, and place through design.",
    summary: `Facing relocation challenges, wHY Architecture sought secure, direct connectivity between home and main offices. TenK Solutions implemented a P2P VPN and Cisco networking to enable secure remote operations. This targeted solution delivered:

- Successfully connected 8 remote workstations to main office
- Enabled secure access to 2TB+ of project files
- 99.9% VPN uptime since implementation
- Supported concurrent access for 15+ users
- Cut remote connectivity costs by 40% vs previous solution
- Seamless integration with existing workflow tools
- Enabled real-time collaboration on large design files
- Reduced IT support calls by 50%

These improvements created a secure, reliable, and cost-effective remote work environment that maintains the firm's high productivity standards while enabling flexible work arrangements.`,
  },
  {
    slug: "phfamoescef",
    companyName: "The PHFAMOESCEF",
    title: "Optimizing IT and Reducing Costs by 27% for PHFAMOESCEF",
    imgSrc: "/img/clients/phfamoescef.webp",
    uri: "https://phfamoescef.org/",
    companyDesc:
      "Prince Hall Free & Accepted Masons & Order of the Eastern Star Charitable Education Foundation.",
    summary: `TenK Solutions restructured PHFAMOESCEF's digital ecosystem, transitioning email from ISP provider to utilizing the Microsoft 365 ecosystem, consolidating WAN and UCaaS services, and migrating from commercial coax to a dedicated fiber setup for WAN and IPTV, allowing them to transition from PBX on POTS to a VOIP UCaaS solution. This comprehensive digital transformation delivered:

- 27% reduction in total IT and telecommunications expenses
- Upgraded internet speeds from 1000/30 Mbps to 200/200 Mbps
- Successfully migrated 15+ email accounts to Microsoft 365
- Eliminated 20+ legacy POTS lines, saving $1,200 monthly
- Deployed 25+ VOIP phones across three facilities
- Consolidated 4 separate vendors down to 2
- Achieved 99.9% uptime with new dedicated fiber connection
- Enhanced security with Microsoft 365 Advanced Threat Protection
- Simplified billing from multiple invoices to single monthly statement
- Successfully integrated IPTV services for 10+ displays

These improvements modernized their infrastructure while significantly reducing costs and complexity, creating a more efficient and reliable technology environment for staff and residents.`,
  },
  {
    slug: "smile-design-studio",
    companyName: "Smile Design Studio of Bethesda",
    title: "Migrating Smile Design Studio to Cloud Solutions",
    imgSrc: "/img/clients/sds.webp",
    uri: "https://smiledesignstudioofbethesda.com/",
    companyDesc:
      "Smile Design Studio of Bethesda focuses on providing high-quality dental services in the Bethesda area.",
    summary: `TenK Solutions facilitated Smile Design Studio's shift from server-based to cloud-based EMR software and developed a custom IT strategy for smooth operations during a growth acquisition. After acquisition, we helped tackle legacy connectivity challenges by working with Comcast on hardware replacements. This comprehensive migration and optimization delivered:

- Zero downtime during the transition process
- Reduced IT infrastructure costs by 45%
- Enhanced security with modern encryption protocols
- Integrated 3 new operatory stations post-acquisition
- Successfully coordinated with Comcast to replace outdated modems and routers
- Achieved HIPAA compliance with new security measures
- Enabled secure remote access for staff

These improvements created a robust, scalable infrastructure that supported the practice's growth while enhancing security, efficiency, and patient care delivery.`,
  },
  {
    slug: "monroe-school",
    companyName: "The Monroe School",
    title: "Upgrading Monroe School's Communication Infrastructure",
    imgSrc: "/img/clients/monroe_school.webp",
    uri: "https://monroeschool.net/",
    companyDesc:
      "The Monroe School offers flexible educational solutions tailored to diverse student needs.",
    summary: `Following a hardware-related communication failure, TenK Solutions upgraded Monroe School's outdated PBX to a modern UCaaS system, enhancing flexibility and reliability while future-proofing their communication infrastructure. Additionally, TenK Solutions optimized the school's network infrastructure through strategic WAP redistribution for optimal coverage, WLAN reprogramming, and removal of unnecessary devices and wiring. These comprehensive improvements resulted in:
- 99.9% uptime for critical communication systems
- 40% reduction in network congestion
- Elimination of Wi-Fi dead zones across main floors
- 40% improvement in overall network performance
- Reduced IT maintenance calls by 60%
- Enhanced security through removal of legacy devices
- Successfully integrated 10+ classroom phones into the new UCaaS platform

These optimizations created a more efficient, reliable, and scalable technology environment that better serves the school's communication and networking needs while reducing operational overhead.`,
  },
  {
    slug: "new-beginnings",
    companyName: "New Beginnings Vocational Program",
    title:
      "Implementing Google Workspace for New Beginnings Vocational Program",
    imgSrc: "/img/clients/nbvps.webp",
    uri: "http://www.newbeginningsvocational.com/",
    companyDesc:
      "New Beginnings Vocational Program equips students with essential skills for career readiness.",
    summary: `TenK Solutions configured the LAN/WLAN and deployed Google Workspace for Education at New Beginnings Vocational Program, along with securing Chromebook licenses to manage assets. The comprehensive solution delivered:

- Successfully deployed Google Workspace for 50+ students and staff
- Configured secure network access across all classrooms
- Implemented enterprise-grade network security protocols
- Managed deployment of 10+ Chromebooks with automated updates
- Achieved 100% adoption rate within first month of implementation
- Implemented content filtering and security policies for CIPA compliance

This cloud-based infrastructure transformation provided a scalable, secure, and manageable IT environment that aligns with the program's mission of preparing students for modern workplace technologies.`,
  },
  {
    slug: "first-chair-pediatric-dentistry",
    companyName: "First Chair Pediatric Dentistry",
    title: "Optimizing IT Resources for First Chair Pediatric Dentistry",
    imgSrc: "/img/clients/first_chair_dentistry.webp",
    uri: "https://firstchairpediatricdentistry.com/",
    companyDesc: `First Chair Pediatric Dentistry specializes in pediatric dental care with a patient-centered approach.`,
    summary: `After receiving a distress call about a front desk PC that was too ill-performing to be productive, TenK Solutions assisted by reallocating resources, moving X-ray and EMR services to dedicated servers and upgrading physical memory. Through strategic infrastructure reorganization and hardware upgrades, the solution delivered:

- 90% faster EMR access and response times
- Dedicated server processing for X-ray imaging, reducing load times by 65%
- Zero front desk workflow interruptions post-implementation
- RAM upgrade from 8GB to 32GB enabling smoother multitasking
- Decreased patient wait times by an average of 5 minutes per check-in
- Eliminated system crashes that previously occurred 2-3 times daily
- Create shared drives for network scanning, improving communications workflow

These improvements significantly enhanced the practice's operational efficiency, leading to improved patient experience and staff satisfaction while maximizing existing hardware investments.`,
  },
];

export const PARTNERS = [
  {
    name: "Lenovo",
    logo: "/img/partners/lenovo.webp",
    website: "https://www.lenovo.com",
    description: "",
  },
  {
    name: "Meraki",
    logo: "/img/partners/meraki.webp",
    website: "https://meraki.cisco.com",
    description: "",
  },
  {
    name: "Astound",
    logo: "/img/partners/astound.webp",
    website: "https://www.astound.com",
    description: "",
  },
  {
    name: "Net2Phone",
    logo: "/img/partners/net2phone.webp",
    website: "https://www.net2phone.com",
    description: "",
  },
  {
    name: "TPx",
    logo: "/img/partners/tpx.webp",
    website: "https://www.tpx.com",
    description: "",
  },
  {
    name: "Google Cloud",
    logo: "/img/partners/google.webp",
    website: "https://www.google.com",
    description: "",
  },
  {
    name: "Fortinet",
    logo: "/img/partners/fortinet.webp",
    website: "https://fortinet.com",
    description: "",
  },
  {
    name: "Informatica",
    logo: "/img/partners/informatica.webp",
    website: "https://www.informatica.com",
    description: "",
  },
];

export const CONTACT_ITEMS = [
  { icon: MdPhone, data: "202.573.7837", hrefPrefix: "tel:+1" },
  {
    icon: MdEmail,
    data: "hello@tenksolutions.com",
    hrefPrefix: "mailto:",
  },
  {
    icon: MdLocationOn,
    data: "Washington Metropolitan Area",
    hrefPrefix: "https://goo.gl/maps/vNNxAbzuHtgj57o58?",
  },
];

export const TESTIMONIALS = [
  [
    "",
    "Game Changer",
    `"Working with [TenK Solutions] has been a game-changer for our business. his expertise has been invaluable in helping us streamline our software development processes and deliver high-quality applications to our customers. Their creative problem-solving skills and ability to work collaboratively with our team have been a true asset. We highly recommend [TenK Solutions] for their exceptional knowledge, skills, and diverse capabilities."`,
    "Dr. Davis",
    "Principal of New Beginnings Vocational Program",
  ],
  [
    "",
    "Next Level",
    `"We have been working with [TenK Solutions] for over a year, and we are extremely impressed with their IT infrastructure management skills. They have taken our IT services to the next level, implementing efficient asset management processes, optimizing our networking setup, and ensuring business continuity. Their attention to detail and proactive approach have been instrumental in keeping our IT systems running smoothly. We highly recommend [TenK Solutions] for their expertise and exceptional service."`,
    "Dr. Logan",
    "Principal of The Monroe School",
  ],
  [
    "",
    "True Professional",
    `"As a data-driven organization, we needed a data visualization expert to help us make sense of our complex data sets. [TenK Solutions] came highly recommended, and they exceeded our expectations. Their expertise in data visualization tools and libraries helped us create interactive visualizations that provided valuable insights and empowered our team to make data-driven decisions. [TenK Solutions] is a true professional with diverse capabilities and excellent collaboration skills. We highly recommend their services."`,
    "Dr. Willis",
    "President of Smile Design Studios of Bethesda",
  ],
];

export const ABOUT_SECTIONS = [
  {
    title: "Who We Are",
    content: [
      // {
      //   text: "TenK Solutions is a premier IT consulting firm founded in 2013 that becomes a trusted technology partner to ambitious organizations. Our experts take the time to deeply understand each client's unique needs in order to provide fully customized solutions that empower them to succeed.",
      // },
      // {
      //   text: "Leveraging decades of combined experience, we architect, implement, and manage complete IT environments and software systems tailored specifically for the challenges our clients face. We handle the complexities of technology so they can focus on their core business. TenK Solutions transforms operations and unlocks potential through strategic, expertly executed IT solutions",
      // },
    ],
  },
];

export const OFFERED_SERVICES: FCardProps[] = [
  [
    "Technology Operations Management",
    "By entrusting your technology operations to us, you can focus on your core business while we handle the complexities of operations as your Business Operations Automation partner.",
    FcElectronics,
    [
      "IT Systems Administration",
      "IT Project Management",
      "Compliance Audits & IT Assessments",
      "Network Monitoring & Endpoint Protection",
      "Vendor/Expense Management",
      "Remote Support & Ticketing",
      "Technical Training & Documentation",
      "Scripting & Automation",
    ],
    "/img/features/ft_people.webp",
    "Reclaim Time",
  ],
  [
    "Technology Brokerage",
    "We advocate for strategies that maximize investments, enable seamless collaboration, ensure data safety, and align technology with core business goals. Through preferred partnerships with over 200 top technology vendors, we design and implement tailored solutions.",
    FcTreeStructure,
    [
      "Unified Communications (UCaaS) ",
      "Contact Center (CCaaS) ",
      "Wi-Fi Systems",
      "Cybersecurity & Disaster Recovery",
      "Connectivity (Wireless, WAN, IoT, mobility)",
      "Cloud & Colocation",
      "Audio/Video,  Surveillance, VOIP/Paging Systems",
      "VR, 3D Printing, CAD Studio Solutions",
    ],
    "/img/features/ft_hardware.webp",
    "Integrate Systems",
  ],
  [
    "Web/Software Development",
    "Unlock your business potential with our expert web and software development services. We specialize in building dynamic, scalable applications using cutting-edge technologies, ensuring optimal performance and user experience.",
    FcCommandLine,
    [
      "Custom Web Applications",
      "Front-end Development (Typescript MVC Frameworks)",
      "Backend Development (Node.js/PHP/Python)",
      "Site Maintenance & Support",
      "Content Management Systems",
      "Internationalization, Localization",
      "API Integrations",
      "Database Integration (SQL/MongoDB)",
      "Cloud / Serverless Solutions",
    ],
    "/img/features/ft_software.webp",
    "Unlock Potential",
  ],
];

export const LEAD_QUESTIONS = [
  {
    question: "Is your business being held back by an outdated phone system?",
    answer:
      "TenK Solutions specializes in implementing feature-rich Unified Communications as a Service (UCaaS) platforms to replace traditional PBXs. Our UCaaS solutions provide scalable enterprise collaboration tools with no hardware costs.",
    cta: "Upgrade Phones",
  },
  {
    question:
      "Are you struggling to deliver an exceptional omni-channel customer experience?",
    answer:
      "Our Contact Center as a Service (CCaaS) solutions enable efficient customer routing across digital channels like voice, chat and SMS. Our CCaaS technology provides global reach, easy integration and on-demand scalability.",
    cta: "Enhance CX",
  },
  {
    question:
      "Are you concerned about the rising cybersecurity threats to your business?",
    answer:
      "TenK Solutions can develop a robust security framework to prevent data breaches and malicious attacks based on your unique risk profile.",
    cta: "Secure Business",
  },
  {
    question:
      "Is your company being limited by inflexible or underperforming network connectivity?",
    answer:
      "Our network solutions team can design and implement the ideal global connectivity services like DIA, broadband, wireless, IoT and mobility to meet your evolving business needs.",
    cta: "Optimize Connectivity",
  },
  {
    question:
      "Are you looking to leverage the scalability and performance of the cloud?",
    answer:
      "Our cloud experts can guide enterprises through adopting cloud computing resources and data center colocation to provide flexible, high-powered IT infrastructure.",
    cta: "Transform IT",
  },
  {
    question:
      "Does your business need an expert technology advisor in the Washington DC metro area?",
    answer:
      "As a leading DC Business Operations Automation firm and Technology Broker, TenK Solutions provides strategic consulting and automation services tailored to area businesses.",
    cta: "Get Local Expertise",
  },
  {
    question:
      "Is outdated software holding back your team's productivity and efficiency?",
    answer:
      "TenK Solutions' custom software development services can design, build and implement modern software solutions to streamline operations.",
    cta: "Boost Productivity",
  },
  {
    question:
      "Are you concerned about the costs of managing IT infrastructure in-house?",
    answer:
      "Our operations automation and management services provide cost-effective, comprehensive support including 24/7 monitoring, maintenance, and vendor management.",
    cta: "Reduce IT Costs",
  },
  {
    question:
      "Are you looking for new ways to maximize your technology investments?",
    answer:
      "TenK Solutions takes a consultative approach as your trusted IT partner to align solutions with business goals and optimize tech spend.",
    cta: "Maximize Investments",
  },
  {
    question:
      "Is it time to upgrade your network design for improved performance and security?",
    answer:
      "As experts in network design and implementation, TenK Solutions can architect an optimized, modern network tailored to your business requirements.",
    cta: "Improve Network",
  },
];

export const FormOptions = {
  industries: [
    "Medical Practice",
    "Dental Office",
    "Law Firm",
    "Private School",
    "Nonprofit Organization",
    "Real Estate",
    "Hospitality",
    "Other",
  ],
  services: [
    "IT Support & Maintenance",
    "Network Setup & Security",
    "Cloud Migration",
    "Data Backup & Recovery",
    "Software Integration",
    "Hardware Procurement",
    "Internet & VoIP Services",
    "Technology Consulting",
    "Special Project",
  ],
  urgencyLevels: ["Immediate", "Within 1-2 Weeks", "Within the Next Month"],
  contactMethods: ["Email", "Phone", "Video Call (via Teams/Zoom)"],
};

// ─── NEW: Business Operations Automation Platform Data ───────────────────────

export interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  priceRange: string;
  billingNote: string;
  href: string;
  color: string;
  gradient: string;
  outcomes: string[];
  deliverables: string[];
  caseStudySlug: string;
  caseStudyStat: string;
}

export const PRODUCTS: Product[] = [
  {
    id: 'client-intake',
    name: 'Client Intake Automation',
    tagline: 'From first contact to paid invoice — automated.',
    description:
      'Every service business manually processes intake. We automate the entire chain: scheduling, follow-ups, form collection, CRM updates, invoice generation, and document delivery.',
    priceRange: '$3,000–$5,000',
    billingNote: 'one-time project',
    href: '/solutions/client-intake',
    color: 'blue.500',
    gradient: 'linear(to-br, blue.400, cyan.500)',
    outcomes: [
      'Eliminate manual scheduling',
      'Auto-generate and send invoices',
      'CRM updated without staff input',
      'Automated follow-up sequences',
      'Digital document collection and storage',
    ],
    deliverables: [
      'Discovery session',
      'Workflow map document',
      'Full implementation',
      'Team training',
      '30 days of support',
    ],
    caseStudySlug: 'smile-design-studio',
    caseStudyStat: '45% reduction in IT infrastructure costs',
  },
  {
    id: 'knowledge-assistant',
    name: 'Internal Knowledge Assistant',
    tagline: 'One AI assistant. All your company knowledge.',
    description:
      'Your team spends hours searching for policies, passwords, processes, and documents. We build one internal chatbot that knows everything your company knows — and answers instantly.',
    priceRange: '$3,000–$5,000',
    billingNote: 'one-time project',
    href: '/solutions/knowledge-assistant',
    color: 'purple.500',
    gradient: 'linear(to-br, purple.400, pink.500)',
    outcomes: [
      'Instant answers for any staff question',
      'Onboarding runs without HR intervention',
      'Policy compliance built into every answer',
      'Works 24/7, never out sick',
      'HIPAA-aware data handling',
    ],
    deliverables: [
      'Knowledge audit',
      'Document ingestion and indexing',
      'AI assistant deployment',
      'Staff training',
      '30 days of support',
    ],
    caseStudySlug: 'new-beginnings',
    caseStudyStat: '100% adoption rate within first month',
  },
  {
    id: 'executive-dashboard',
    name: 'Executive Dashboard',
    tagline: 'Your entire business on one screen.',
    description:
      'Revenue, sales pipeline, open projects, support tickets, invoices, and AI-generated summaries — all in one real-time view. Stop running your business from spreadsheets.',
    priceRange: '$3,000–$5,000',
    billingNote: 'one-time project',
    href: '/solutions/executive-dashboard',
    color: 'green.500',
    gradient: 'linear(to-br, green.400, teal.500)',
    outcomes: [
      'Real-time KPIs in one view',
      'AI-generated weekly summaries',
      'Connect QuickBooks, HubSpot, Stripe and more',
      'Anomaly alerts before they become problems',
      'Mobile-friendly executive access',
    ],
    deliverables: [
      'Data source audit',
      'Dashboard architecture',
      'Integration build',
      'AI summary configuration',
      '30 days of support',
    ],
    caseStudySlug: 'phfamoescef',
    caseStudyStat: '27% reduction in total IT and telecom expenses',
  },
  {
    id: 'telecom-cloud-optimization',
    name: 'Telecom & Cloud Brokerage',
    tagline: 'Better infrastructure. Zero carrier bias. Lower costs.',
    description:
      'We audit your current telecom, UCaaS, CCaaS, and cloud spend. Using our 200+ supplier partnerships, we negotiate the best rates, upgrade your infrastructure, and handle the migration—saving you money while upgrading capabilities.',
    priceRange: 'Free Audit',
    billingNote: 'revenue generated via supplier partners',
    href: '/solutions/telecom-cloud',
    color: 'orange.500',
    gradient: 'linear(to-br, orange.400, yellow.500)',
    outcomes: [
      'Average of 20-30% reduction in telecom spend',
      'Modernize legacy PBX to cloud-based UCaaS/CCaaS',
      'Consolidate multiple vendor bills into one',
      'Expert migration and project management included',
      'Zero cost to you—we are paid by the vendors',
    ],
    deliverables: [
      'Comprehensive telecom bill audit',
      'Supplier agnostic quotes (3-5 options)',
      'Implementation and project management',
      'Ongoing vendor management',
      'Quarterly rate reviews',
    ],
    caseStudySlug: 'monroe-school',
    caseStudyStat: '99.9% uptime and upgraded PBX to UCaaS',
  },
];

export interface Package {
  name: string;
  price: string;
  billing: string;
  tagline: string;
  description: string;
  features: string[];
  highlighted: boolean;
  cta: string;
  ctaHref: string;
  badge?: string;
}

export const PACKAGES: Package[] = [
  {
    name: 'Launch',
    price: 'Starting at $3,000',
    billing: 'one-time',
    tagline: 'Automate one business process.',
    description:
      'The fastest way to prove ROI. We identify your highest-friction process, map it, and automate it end-to-end.',
    features: [
      'Discovery & workflow mapping session',
      'One fully automated business process',
      'Platform setup and configuration',
      'Team training (up to 5 staff)',
      '30 days of post-launch support',
      'Documentation of what was built',
    ],
    highlighted: false,
    cta: 'Start With Launch',
    ctaHref: '/contact-us?package=launch',
  },
  {
    name: 'Optimize',
    price: 'Starting at $8,000',
    billing: 'one-time',
    tagline: 'Three connected processes + a knowledge base.',
    description:
      'For businesses ready to systematize. We connect three of your core processes, add an AI knowledge assistant, and audit your telecom/cloud infrastructure to ensure you have the bandwidth and tools to run it.',
    features: [
      'Everything in Launch',
      'Three fully automated processes',
      'Internal knowledge assistant deployment',
      'Telecom & Cloud Infrastructure Audit',
      'Third-party integrations (CRM, EHR, accounting)',
      'Team training (up to 15 staff)',
      '60 days of post-launch support',
    ],
    highlighted: true,
    badge: 'Most Popular',
    cta: 'Optimize My Operations',
    ctaHref: '/contact-us?package=optimize',
  },
  {
    name: 'Operate',
    price: '$500–$1,500/month',
    billing: 'monthly',
    tagline: 'Continuous improvements, monitoring, and support.',
    description:
      'Your operations co-pilot. We monitor, maintain, and continuously improve your automation platform — and build new workflows as your business evolves.',
    features: [
      'Platform monitoring and maintenance',
      'Up to 2 new workflows per month',
      'Priority support (4-hour response SLA)',
      'Monthly performance report',
      'Quarterly strategy review',
      'Unlimited staff seats',
    ],
    highlighted: false,
    cta: 'Start Continuous Operations',
    ctaHref: '/contact-us?package=operate',
  },
];

export interface Industry {
  id: string;
  name: string;
  icon: string;
  painPoint: string;
  solution: string;
  caseStudySlug: string;
  stat: string;
  statLabel: string;
  href: string;
}

export const INDUSTRIES: Industry[] = [
  {
    id: 'healthcare',
    name: 'Healthcare & Dental',
    icon: '🏥',
    painPoint: 'Patient intake is manual, compliance is a constant worry, and staff spend half their day on paperwork.',
    solution: 'Automate patient intake, build a HIPAA-aware knowledge assistant, and give your practice director a real-time operations dashboard.',
    caseStudySlug: 'smile-design-studio',
    stat: '45%',
    statLabel: 'reduction in IT costs',
    href: '/industries#healthcare',
  },
  {
    id: 'aec',
    name: 'Architecture & Engineering',
    icon: '🏗️',
    painPoint: 'Project files are scattered, remote collaboration is painful, and outsourcing drafting costs a fortune.',
    solution: 'Centralize project knowledge, enable secure remote file access, and automate project intake and handoff workflows.',
    caseStudySlug: 'determined-by-design',
    stat: '$45K',
    statLabel: 'outsourcing costs eliminated',
    href: '/industries#aec',
  },
  {
    id: 'education',
    name: 'Education',
    icon: '🎓',
    painPoint: 'Staff onboarding is inconsistent, communication infrastructure is outdated, and IT maintenance pulls teachers away from students.',
    solution: 'Deploy an AI knowledge assistant for staff processes, modernize communications with UCaaS, and automate onboarding workflows.',
    caseStudySlug: 'monroe-school',
    stat: '60%',
    statLabel: 'reduction in IT maintenance calls',
    href: '/industries#education',
  },
  {
    id: 'nonprofit',
    name: 'Nonprofit',
    icon: '🤝',
    painPoint: 'Multiple vendors, rising telecom costs, and manual grant and donor intake processes drain limited resources.',
    solution: 'Consolidate vendors, automate donor and grant intake, and deliver cost savings you can report to your board.',
    caseStudySlug: 'phfamoescef',
    stat: '27%',
    statLabel: 'reduction in total expenses',
    href: '/industries#nonprofit',
  },
  {
    id: 'professional-services',
    name: 'Professional Services',
    icon: '💼',
    painPoint: 'Client onboarding is slow, staff questions create constant interruptions, and leadership has no real-time visibility into the business.',
    solution: 'Automate client intake and onboarding, deploy a knowledge assistant, and build an executive dashboard for real-time decision-making.',
    caseStudySlug: 'why-architecture',
    stat: '50%',
    statLabel: 'reduction in IT support calls',
    href: '/industries#professional-services',
  },
];

export interface PlatformModule {
  name: string;
  description: string;
  icon: string;
  category: 'Core' | 'AI' | 'Workflow' | 'Admin';
}

export const PLATFORM_MODULES: PlatformModule[] = [
  { name: 'Authentication', description: 'SSO, MFA, and role-based access control', icon: '🔐', category: 'Core' },
  { name: 'Organizations', description: 'Multi-tenant client isolation and management', icon: '🏢', category: 'Core' },
  { name: 'Users & Permissions', description: 'Granular access control down to the field level', icon: '👥', category: 'Core' },
  { name: 'Documents', description: 'Upload, version, search, and share with audit trail', icon: '📄', category: 'Core' },
  { name: 'AI Gateway', description: 'LLM-powered assistant layer with retrieval-augmented generation', icon: '🧠', category: 'AI' },
  { name: 'Vector Search', description: 'Semantic document search across your knowledge base', icon: '🔍', category: 'AI' },
  { name: 'Workflow Engine', description: 'Visual automation builder with conditional logic and scheduling', icon: '⚙️', category: 'Workflow' },
  { name: 'Forms & Approvals', description: 'Dynamic forms with multi-step routing and approvals', icon: '📋', category: 'Workflow' },
  { name: 'Notifications', description: 'Email, SMS, Slack, and Teams delivery on any trigger', icon: '🔔', category: 'Workflow' },
  { name: 'Billing', description: 'Stripe integration with usage-based pricing support', icon: '💳', category: 'Admin' },
  { name: 'Audit Logs', description: 'Immutable compliance trail for every action', icon: '📊', category: 'Admin' },
  { name: 'Admin Portal', description: 'Full client and system management interface', icon: '🖥️', category: 'Admin' },
  { name: 'API & Webhooks', description: 'REST API and webhooks for any integration', icon: '🔗', category: 'Admin' },
  { name: 'Analytics', description: 'Usage, performance, and business KPI dashboards', icon: '📈', category: 'Admin' },
];

export const SOCIAL_PROOF_STATS = [
  { value: '$45K', label: 'Outsourcing costs eliminated', icon: '💰' },
  { value: '27%', label: 'Telecom cost reduction', icon: '📉' },
  { value: '90%', label: 'Faster EMR access', icon: '⚡' },
  { value: '200+', label: 'Technology partners', icon: '🤝' },
  { value: '99.9%', label: 'Uptime guaranteed', icon: '🛡️' },
  { value: '7+', label: 'Industries served', icon: '🏢' },
];
